import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ExternalLink, GraduationCap, Loader2 } from 'lucide-react';
import { adminApi } from '../services/api';
import { Pastille, Vide } from '../components/suivi/ui';
import { annoncerErreur, EtatErreur, Squelette, SqueletteCarte } from '../etats';

/* Le coach suit lui aussi les cours de l'Academy : meme catalogue que les
   associations, connexion directe (SSO) et inscription au clic. */
export default function MesFormations() {
  const [modules, setModules] = useState(null);
  const [progression, setProgression] = useState({});
  const [erreur, setErreur] = useState(null);
  const [essai, setEssai] = useState(0);
  const [enCours, setEnCours] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let vivant = true;
    setErreur(null);
    adminApi.getModules()
      .then((liste) => vivant && setModules((Array.isArray(liste) ? liste : []).filter((m) => String(m.id).startsWith('ACAD'))))
      // L'objet entier : son statut dit lequel des cas afficher.
      .catch((e) => vivant && setErreur(e));
    adminApi.academyProgression()
      .then((liste) => vivant && Array.isArray(liste) && setProgression(Object.fromEntries(liste.map((c) => [c.module_id, c]))))
      .catch(() => {});
    return () => { vivant = false; };
  }, [essai]);

  const inscrits = useMemo(() => Object.keys(progression).length, [progression]);

  // La fenetre s'ouvre pendant le clic : ouverte apres l'appel reseau, le
  // navigateur la bloquerait comme fenetre surgissante.
  const ouvrir = async (moduleId) => {
    const fenetre = window.open('', '_blank');
    if (fenetre) fenetre.opener = null;
    setEnCours(moduleId);
    setMessage('');
    try {
      const rep = await adminApi.academySso(moduleId);
      if (!rep?.url) throw new Error("Connexion à l'Academy impossible pour le moment.");
      if (rep.mode === 'connexion') {
        setMessage("Vous avez déjà un compte Academy avec mot de passe : connectez-vous avec vos identifiants habituels.");
      }
      if (fenetre) fenetre.location.href = rep.url;
      else window.location.assign(rep.url);
    } catch (e) {
      if (fenetre) fenetre.close();
      annoncerErreur(e.message || "Connexion à l'Academy impossible pour le moment.");
    } finally {
      setEnCours('');
    }
  };

  if (erreur && !modules) {
    return (
      <div className="bg-[var(--bg-card)] rounded-carte shadow-carte">
        <EtatErreur erreur={erreur} onReessayer={() => setEssai((n) => n + 1)} />
      </div>
    );
  }
  if (!modules) {
    return (
      <div className="space-y-6">
        <div>
          <Squelette className="h-3 w-28 mb-3" />
          <Squelette className="h-8 w-80 max-w-full mb-3" />
          <Squelette className="h-3 w-96 max-w-full" />
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <SqueletteCarte key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
      <header>
        <p className="sous-titre-page">Mes formations</p>
        <h1 className="titre-page">Parcours ASSO-PRO sur l'Academy</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1 max-w-2xl">
          Les mêmes cours que les associations que vous accompagnez. Un clic vous connecte à l'Academy et vous inscrit au cours.
          {inscrits > 0 && ` Vous suivez ${inscrits} cours.`}
        </p>
      </header>
      {message && <p className="encart encart-attention" role="status">{message}</p>}

      {modules.length === 0 ? (
        <Vide icone={GraduationCap} titre="Aucun cours disponible" texte="Le catalogue de l'Academy n'est pas encore synchronisé." />
      ) : (
        <ul className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {modules.map((m) => {
            const suivi = progression[m.id];
            const pct = suivi?.progression != null ? Math.round(suivi.progression) : null;
            return (
              <li key={m.id} className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5 flex flex-col">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className="text-xs font-semibold text-[var(--accent-texte)]">{m.domaine_nom}</span>
                  {suivi?.acheve ? (
                    <Pastille className="text-[var(--vert-fonce)] bg-[var(--vert-pale)]"><CheckCircle2 className="w-3 h-3" />Terminé</Pastille>
                  ) : suivi ? (
                    <Pastille className="text-[var(--text-bold)] bg-[var(--info-fond)]">Inscrit</Pastille>
                  ) : null}
                </div>
                <h2 className="titre-carte text-[var(--text-bold)] mb-1">{m.titre}</h2>
                {m.description && <p className="text-sm text-[var(--text-muted)] line-clamp-3 mb-3">{m.description}</p>}
                <div className="mt-auto">
                  {pct != null && (
                    <div className="mb-3">
                      <div className="flex justify-between text-xs text-[var(--text-muted)] mb-1"><span>Progression</span><span className="tabular-nums">{pct} %</span></div>
                      <div className="h-1.5 rounded-controle bg-[var(--bg-main)] overflow-hidden"><div className="h-full bg-[var(--accent)]" style={{ width: `${pct}%` }} /></div>
                    </div>
                  )}
                  <button type="button" onClick={() => ouvrir(m.id)} disabled={enCours === m.id}
                    aria-busy={enCours === m.id || undefined}
                    className="act w-full">
                    {enCours === m.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                    {suivi ? 'Reprendre le cours' : 'Suivre sur l’Academy'}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </motion.div>
  );
}